import * as React from 'react';
import PropTypes from 'prop-types';
import { useInstance } from '../logging/index.js';
import { FingerprintError, LoadingTimeoutError, InitializationError } from './errors.js';
import * as errors from './errors.js';
export { errors as Errors };
import { SeonContext } from './context.js';
import { once } from '../utils/functions.js';

/**
 * Supported CDN hosts
 *
 * @enum {string}
 * @readonly
 *
 * @access public
 */

var CdnHost = {
  DeviceinfCom: "deviceinf.com",
  GetdeviceinfCom: "getdeviceinf.com",
  SeondfCom: "seondf.com",
  SeonIo: "seon.io"
};
/**
 * Create promise that can be resolved/rejected from outside its body
 *
 * @access private
 */

function usePromise() {
  return React.useMemo(() => {
    var outerReject;
    var outerResolve;
    var promise = new Promise((resolve, reject) => {
      outerReject = reject;
      outerResolve = resolve;
    });
    return [promise, outerReject, outerResolve];
  }, []);
}
/**
 * React Context provider for SEON SDK
 *
 * SEON SDK source is hosted on multiple CDNs to avoid browsers and extensions
 * that block trackers. If SEON SDK fails to load, try another `host`.
 *
 * Session ID must be unique per visitor. Common approaches would be either to
 * generate one and store it in cookies or use already existing cookies, e.g.
 * Google Analytics session ID.
 *
 * @param {Object} props Component props
 * @param {number} props.attempts Retry attempts number
 * @param {boolean} props.disabled Disables SEON integration
 * @param {CdnHost} props.host CDN host to use
 * @param {RenderScriptCallback} props.renderScript See example
 * @param {string} props.sessionId Visitor session ID
 * @param {number} props.timeout Retry timeout
 * @param {string} props.version SEON agent version
 *
 * @example // Basic usage with React Helmet
 * // Herz does not manage <head>; <script> tag injection is up to consumer
 * <Herz.Seon.FingerprintProvider
 *   host="deviceinf.com"
 *   renderScript={(props) => (
 *     <Helmet>
 *       <script {...props} />
 *     </Helmet>
 *   )}
 *   sessionId={sessionId}
 * />
 *
 * @example // Usage with feature flags
 * // This will not wrap children with context provider and force SEON hooks
 * // to use no-op methods.
 * const isSeonEnabled = useFeatureFlag("SEON")
 *
 * <Herz.Seon.FingerprintProvider
 *   disabled={!isSeonEnabled}
 *   host="deviceinf.com"
 *   renderScript={renderScript}
 *   sessionId={sessionId}
 * />
 *
 * @access public
 */


function FingerprintProvider(props) {
  // SEON cannot run on server-side or without sessionId
  if (!process.browser || !props.sessionId) {
    return props.children;
  }

  var logger = useInstance();
  var [active, setActive] = React.useState(false);
  var [initialized, setInitialized] = React.useState(false);
  var [fingerprint, rejectFingerprint, resolveFingerprint] = usePromise();
  var handleActivation = React.useMemo(() => once(function _handleActivation() {
    logger.trace("[seon] Activating..."); // Activate asynchronously to avoid "bad setState() call" error

    setTimeout(() => setActive(true), 1);
  }), []);
  var value = React.useMemo(() => ({
    activate() {
      handleActivation();
    },

    resolveFingerprint() {
      logger.trace("[seon] Resolving fingerprint...");
      return fingerprint;
    }

  }), [fingerprint, handleActivation]);
  React.useEffect(() => {
    var attempts = props.attempts;
    var id;

    function initialize() {
      if (!window.seon) {
        logger.debug("[seon] Loading...");
        attempts -= 1;

        if (attempts <= 0) {
          return rejectFingerprint(new LoadingTimeoutError());
        }

        id = setTimeout(initialize, props.timeout);
        return;
      }

      logger.debug("[seon] Initializing...");
      logger.trace("[seon] Initializing with host: \"".concat(props.host, "\" and session_id: \"").concat(props.sessionId, "\""));
      window.seon.config({
        host: props.host,
        session_id: props.sessionId,
        audio_fingerprint: true,
        canvas_fingerprint: true,
        webgl_fingerprint: true,
        onSuccess: function onSuccess() {
          logger.debug("[seon] Initializing completed");
          setInitialized(true);
        },
        onError: function onError(message) {
          logger.debug("[seon] Initializing failed");
          logger.error(message);
          rejectFingerprint(new InitializationError());
        }
      });
    }

    if (active) {
      initialize();
      return () => {
        logger.trace("[seon] Initializing cancelled");
        clearTimeout(id);
      };
    }
  }, [active, logger, props.attempts, props.host, props.sessionId, props.timeout, rejectFingerprint]);
  React.useEffect(() => {
    if (initialized) {
      window.seon.getBase64Session(data => {
        if (data) {
          resolveFingerprint(data);
        } else {
          rejectFingerprint(new FingerprintError());
        }
      });
    }
  }, [initialized, rejectFingerprint, resolveFingerprint]);

  if (props.disabled) {
    return props.children;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, active ? props.renderScript({
    src: "https://cdn.".concat(props.host, "/js/").concat(props.version, "/agent.js"),
    defer: true
  }) : null, /*#__PURE__*/React.createElement(SeonContext.Provider, {
    value: value
  }, props.children));
}
FingerprintProvider.defaultProps = {
  attempts: 20,
  host: CdnHost.GetdeviceinfCom,
  timeout: 250,
  version: "v5"
};
FingerprintProvider.displayName = "Seon.FingerprintProvider";
FingerprintProvider.propTypes = {
  attempts: PropTypes.number,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  host: PropTypes.oneOf(Object.values(CdnHost)),
  version: PropTypes.oneOf(["v4", "v5"]),

  /**
   * React Helmet workaround; see provider usage example
   */
  renderScript: PropTypes.func,
  sessionId: PropTypes.string.isRequired,
  timeout: PropTypes.number
};
/**
 * Retrieve device fingerprint from SEON
 *
 * @access public
 */

function useFingerprint() {
  var context = React.useContext(SeonContext);
  var [error, setError] = React.useState();
  var [fetching, setFetching] = React.useState(true);
  var [value, setValue] = React.useState();
  context.activate();
  var rawValue = React.useMemo(() => context.resolveFingerprint(), [context]);
  React.useEffect(() => {
    rawValue.then(value => {
      setFetching(false);
      setValue(value);
      return value;
    }).catch(error => {
      setFetching(false);
      setError(error);
    });
  }, [rawValue]);
  return {
    /**
     * Error object if operation failed
     *
     * @type {(Error|undefined)}
     */
    error,

    /**
     * Operation state
     *
     * @type {boolean}
     */
    fetching,

    /**
     * Original promise resolving to fingerprint
     *
     * @type {Promise<string>}
     */
    rawValue,

    /**
     * Fingerprint or undefined if fetching or unable to fetch
     *
     * @type {string}
     */
    value
  };
}

export { CdnHost, FingerprintProvider, useFingerprint };
